import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, SITE } from "../lib/constants";

export const Navbar = ({ onCTAClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div
          className={`flex items-center justify-between rounded-full px-5 sm:px-6 py-3 transition-all duration-500 ${
            scrolled
              ? "glass border border-white/5"
              : "border border-transparent"
          }`}
        >
          <a
            href="#top"
            data-testid="nav-logo"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-2"
          >
            <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E6C35C] to-[#A88824] flex items-center justify-center text-black font-bold text-sm shadow-[0_0_20px_rgba(212,175,55,0.4)]">
              D
            </span>
            <span className="font-display text-lg tracking-tight text-white">
              {SITE.name}
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => handleClick(l.href)}
                className="nav-underline text-sm text-[#B0B0B0] hover:text-[#D4AF37] transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cta-button"
              onClick={onCTAClick}
              className="btn-gold-glow hidden sm:inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C35C] text-black text-sm font-semibold rounded-full px-5 py-2.5 transition-all shadow-[0_0_20px_rgba(212,175,55,0.25)] hover:shadow-[0_0_30px_rgba(230,195,92,0.45)]"
            >
              Book Consultation <ArrowUpRight className="w-4 h-4" />
            </button>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((o) => !o)}
              className="md:hidden text-white p-2"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="md:hidden mt-3 glass rounded-2xl p-4 flex flex-col gap-1"
            >
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => handleClick(l.href)}
                  className="text-left text-[#B0B0B0] hover:text-[#D4AF37] py-2 px-2"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta"
                onClick={() => {
                  setOpen(false);
                  onCTAClick();
                }}
                className="mt-2 bg-[#D4AF37] text-black font-semibold rounded-full px-5 py-3"
              >
                Book Free Consultation
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};
